// Components==============
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import indexActivity from 'actions/activity/index';
import updateProject from 'actions/project/update';
import Token from 'components/UI/Token/Token';
import { useAlert } from 'hooks/useAlert';
import { useMediaQ } from 'hooks/useMediaQ';
import { usePermission } from 'hooks/usePermission';
import { useRedux } from 'hooks/useRedux';
import { useReduxAction } from 'hooks/useReduxAction';
import { useTranslate } from 'hooks/useTranslate';
import { ProjectDetailsContext } from 'pages/Workspace/ProjectDetails/ProjectDetails';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { PageType } from 'typings';
import { GlobalBuildingBlocksMountedContext } from 'utilities/GlobalContext/GlobalBuildingBlocksMounted';
import { getNoContentItems } from 'utilities/getNoContentItems';
import Button from '../Button/Button';
import RichText from '../RichText/RichText';
import Spinner from '../Spinner/Spinner';
import './NoContent.module.scss';

// =========================

export default function NoContent({
  page,
  loading,
  hasContent,
  isTeacherMember,
  children,
  loadOnce,
  decreasedHeight,
  handleClick,
  sharecode,
  marginTop,
  returnNothing = false,
  project_id,
}: {
  page: PageType;
  loading: boolean;
  hasContent: boolean;
  isTeacherMember?: boolean;
  children: React.ReactNode;
  loadOnce?: boolean;
  decreasedHeight?: 50 | 70;
  handleClick?: () => void;
  sharecode?: React.ReactNode;
  marginTop?: string;
  returnNothing?: boolean;
  project_id?: number;
}) {
  const translate = useTranslate();
  const { img, title, description, buttons, conditions } = getNoContentItems(
    page,
    translate
  );

  const { buildingBlocksMounted, setBuildingBlocksMounted } = useContext(
    GlobalBuildingBlocksMountedContext
  );

  const { setIsEditable } = useContext(ProjectDetailsContext);

  const [selectedFile, setSelectedFile] = useState(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [randomToken, setRandomToken] = useState<ActivityEntity>();
  const [tokenInterval, setTokenInterval] = useState(0);
  const [largeFile, setLargeFile] = useState(false);
  const [emptyProject, setEmptyProject] = useState(false);
  const hiddenFileInput = useRef(null);

  const dispatch = useReduxAction();
  const history = useHistory();
  const alert = useAlert();

  // When upload image pressed
  const handleScan = (event) => {
    hiddenFileInput.current.click();
    setEmptyProject(false);
  };

  useEffect(() => {
    dispatch(indexActivity());
  }, []);

  const activities = useRedux((state) => {
    const data = state.activity?.entities.allIds
      .map((id) => state.activity?.entities.byId[id])
      .filter((a) => a?.type === 'regular')
      // filter vrije activiteiten duplicates
      .filter(
        (value, i, arr) =>
          arr.findIndex((activity) => activity?.name === value?.name) === i
      )
      .filter((activity) => activity?.slug !== 'onbekend');

    return data;
  });

  // For loading animation
  const getRandomToken = () => {
    const tokenLength = activities?.length - 5;
    const randomIndex = parseInt(Math.random() * tokenLength) + 1;
    const token = activities?.find((activity) => activity?.id === randomIndex);
    return token;
  };

  // Upload of image file
  const onFileUpload = (file) => {
    setScanLoading(true);

    // Animation loop
    const tokenInterval = setInterval(() => {
      setRandomToken(getRandomToken());
    }, 500);
    setTokenInterval(tokenInterval);

    dispatch(
      updateProject({
        processMlImage: true,
        image: file,
        id: project_id,
      })
    )
      .then((project: ProjectProjectEntity) => {
        clearInterval(tokenInterval);
        setScanLoading(false);
        if (project.process?.length === 0) {
          console.log('empty project');
          alert(translate('scan.error.process-not-recognized'));
          setEmptyProject(true);
        } else {
          history.push(`/werkplaats/project-details/${project.id}`);
          setIsEditable(true);
        }
      })
      .catch((error) => {
        clearInterval(tokenInterval);
        setScanLoading(false);
        alert(translate('scan.error.process-not-recognized'));
      });
  };

  // Selection of image file
  const onFileChange = (event) => {
    // Update the state
    const file = event.target.files[0];
    setSelectedFile(file);
    console.log(file);
    setRandomToken(getRandomToken());

    // Cap on image size and informs user
    if (event.target.files[0].size > 5000000) {
      setLargeFile(true);
    } else {
      setLargeFile(false);
      onFileUpload(file);
    }
  };

  useEffect(() => {
    if (loadOnce && !buildingBlocksMounted) {
      const timeout = setTimeout(() => {
        setBuildingBlocksMounted(true);
      }, 3000);

      return () => clearTimeout(timeout);
    }
  }, []);

  const query = useMediaQ('min', 1024);
  const isTeacher = usePermission(['Docent', 'super-admin']);

  if (isTeacherMember && !hasContent)
    return (
      <div
        styleName="nocontent"
        style={{
          height: decreasedHeight ? `${decreasedHeight}%` : '',
          marginTop,
        }}
      >
        <div styleName="content-wrapper">
          <div styleName="image-wrapper">
            <img alt={page} src={`/images/error-illustrations/${img}.svg`} />
          </div>
          <h1>{translate('project.student-no-process')}</h1>
        </div>
      </div>
    );

  if (returnNothing && !hasContent) return <></>;

  if (loadOnce && !buildingBlocksMounted && !hasContent)
    return <Spinner fullPage />;

  if (loading && !hasContent)
    return (
      <Spinner
        fullPage
        style={{ height: decreasedHeight ? `${decreasedHeight}%` : '' }}
      />
    );

  if (hasContent) return <>{children}</>;

  // Scanning animation while scanning image
  if (scanLoading && !hasContent)
    return (
      <div
        styleName="nocontent"
        style={{
          height: decreasedHeight ? `${decreasedHeight}%` : '',
          marginTop,
        }}
      >
        <div styleName="content-wrapper">
          <div styleName="token">
            {randomToken && 'thumbnail' in randomToken && (
              <Token token={randomToken} />
            )}
            <h3>{translate('project.actions.scanning')}</h3>
          </div>
        </div>
      </div>
    );

  return (
    <div
      styleName="nocontent"
      style={{
        height: decreasedHeight ? `${decreasedHeight}%` : '',
        marginTop,
      }}
    >
      <div styleName="content-wrapper">
        <div styleName="image-wrapper">
          <img alt={page} src={`/images/error-illustrations/${img}.svg`} />
        </div>
        {!(conditions?.includes('teacher') && !isTeacher) && (
          <>
            {page === 'project-scan' ? (
              <div styleName="beta-div">
                <>
                  <h1 styleName="beta-h">{title}</h1>
                </>
                <>
                  <img
                    alt="beta"
                    src="/images/beta-blue.svg"
                    styleName="beta-img"
                  />
                </>
              </div>
            ) : (
              <h1>{title}</h1>
            )}
            <div styleName="rich-text">
              <RichText content={description} />
            </div>
            {buttons && (
              <div styleName="buttons-wrapper">
                {buttons.map((button, index) => {
                  if (button.conditions?.includes('teacher') && !isTeacher)
                    return null;
                  if (button.conditions?.includes('scan') && !query)
                    return (
                      <>
                        <Button
                          key={index}
                          to={button.to ? button.to : undefined}
                          onClick={handleScan}
                        >
                          {button.name}
                          {button.icon && (
                            <FontAwesomeIcon icon={button.icon} />
                          )}
                        </Button>
                        <input
                          key={index + 1}
                          type="file"
                          accept="image/*"
                          ref={hiddenFileInput}
                          onChange={onFileChange}
                        />
                        <Button
                          key={index + 2}
                          to={`/mlscan/${project_id}`}
                          onClick={handleClick}
                        >
                          {translate('project.actions.add-scan-process')}
                        </Button>
                      </>
                    );
                  if (button.conditions?.includes('scan'))
                    return (
                      <>
                        <Button
                          key={index}
                          to={button.to ? button.to : undefined}
                          onClick={handleScan}
                        >
                          {button.name}
                          {button.icon && <img alt="beta" src={button.icon} />}
                        </Button>
                        <input
                          key={index + 1}
                          type="file"
                          accept="image/*"
                          ref={hiddenFileInput}
                          onChange={onFileChange}
                        />
                      </>
                    );

                  return (
                    <Button
                      key={index}
                      to={button.to ? button.to : undefined}
                      onClick={!button.to ? handleClick : undefined}
                    >
                      {button.name}
                      {button.icon && <FontAwesomeIcon icon={button.icon} />}
                    </Button>
                  );
                })}
              </div>
            )}
            {largeFile && (
              <div styleName="largeFile">
                <p>{translate('project.errors.scan-photo-large')}</p>
              </div>
            )}
            {sharecode && (
              <div styleName="sharecode">
                <p>{translate('nocontent.sharecode')}</p>
                {sharecode}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
